import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { useMembersApi } from '@/hooks/useMembersApi';
import { useScoresApi } from '@/hooks/useScoresApi';
import { cn } from '@/lib/utils';
import { useAuthStore, useEffectiveRole } from '@/stores/authStore';
import {
  Building2,
  Calendar,
  Home,
  LayoutDashboard,
  Loader2,
  Settings,
  Shield,
  Swords,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const tabs = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'workouts', label: 'Workouts', icon: Target },
  { id: 'friends', label: 'Friends', icon: Users },
  { id: 'challenges', label: 'Challenges', icon: Swords },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'connect', label: 'Connect', icon: Zap },
];

// Role display labels
const roleLabels: Record<string, string> = {
  member: 'Member',
  affiliate_owner: 'Owner',
  affiliate_coach: 'Coach',
  individual: 'Individual',
};

const EMPTY_HUB_STATS = {
  totalScores: 0,
  globalRank: 0,
  recentScoresCount: 0,
};

export default function MyHub() {
  const { user } = useAuthStore();
  const activeProfileId = useAuthStore((s) => s.activeProfile?.id);
  const activeProfileIdRef = useRef(activeProfileId);
  const previousActiveProfileIdRef = useRef(activeProfileId);
  const profileRequestGenerationRef = useRef(0);
  activeProfileIdRef.current = activeProfileId;
  const membersApi = useMembersApi();
  const individualsApi = useIndividualsApi();
  const scoresApi = useScoresApi();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [affiliateName, setAffiliateName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hubStats, setHubStats] = useState({
    totalScores: 0,
    globalRank: 0,
    recentScoresCount: 0,
  });

  // My Workouts — scores joined with workout data (BUG-APR07-001)
  type MyScore = {
    id: string;
    workout_id: string;
    workout_name: string;
    value: number;
    division: string;
    score_display: string;
    scoring_type: string;
    notes: string | null;
    created_at: string;
  };
  const [myScores, setMyScores] = useState<MyScore[]>([]);
  const [isLoadingScores, setIsLoadingScores] = useState(false);
  const [scoresError, setScoresError] = useState<string | null>(null);

  const fetchMyScores = useCallback(async () => {
    const requestProfileId = activeProfileId;
    const requestGeneration = profileRequestGenerationRef.current;
    setIsLoadingScores(true);
    setScoresError(null);
    try {
      const response = await scoresApi.getScores({ limit: 50 });
      if (
        requestGeneration !== profileRequestGenerationRef.current ||
        requestProfileId !== activeProfileIdRef.current
      ) {
        return;
      }
      if (response.success && response.data) {
        setMyScores(response.data.scores);
      } else {
        setScoresError(response.error || 'Failed to load workouts');
      }
    } catch (err) {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        console.error('Failed to fetch my scores:', err);
        setScoresError('Failed to load workouts');
      }
    } finally {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        setIsLoadingScores(false);
      }
    }
  }, [scoresApi, activeProfileId]);

  const effectiveRole = useEffectiveRole();

  // Determine if this role has an affiliate
  const hasAffiliate =
    effectiveRole === 'member' ||
    effectiveRole === 'affiliate_owner' ||
    effectiveRole === 'affiliate_coach';

  useEffect(() => {
    if (previousActiveProfileIdRef.current === activeProfileId) return;

    previousActiveProfileIdRef.current = activeProfileId;
    profileRequestGenerationRef.current += 1;
    setAffiliateName(null);
    setHubStats(EMPTY_HUB_STATS);
    setMyScores([]);
    setScoresError(null);
    setIsLoading(false);
    setIsLoadingScores(false);
  }, [activeProfileId]);

  // Fetch dashboard data for members/affiliate staff
  const fetchAffiliateData = useCallback(async () => {
    if (!hasAffiliate) {
      return;
    }

    const requestProfileId = activeProfileId;
    const requestGeneration = profileRequestGenerationRef.current;
    setIsLoading(true);
    try {
      const response = await membersApi.getDashboard();
      if (
        requestGeneration !== profileRequestGenerationRef.current ||
        requestProfileId !== activeProfileIdRef.current
      ) {
        return;
      }
      if (response.success && response.data) {
        const dashData = response.data;
        setAffiliateName(dashData.member.affiliate_name);
        setHubStats((prev) => ({
          ...prev,
          recentScoresCount: dashData.recent_scores?.length || 0,
        }));
      }
    } catch (err) {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        console.error('Failed to fetch affiliate data:', err);
      }
    } finally {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        setIsLoading(false);
      }
    }
  }, [membersApi, hasAffiliate, activeProfileId]);

  // Fetch individual dashboard data
  const fetchIndividualData = useCallback(async () => {
    if (effectiveRole !== 'individual') {
      return;
    }

    const requestProfileId = activeProfileId;
    const requestGeneration = profileRequestGenerationRef.current;
    setIsLoading(true);
    try {
      const response = await individualsApi.getDashboard();
      if (
        requestGeneration !== profileRequestGenerationRef.current ||
        requestProfileId !== activeProfileIdRef.current
      ) {
        return;
      }
      if (response.success && response.data) {
        setHubStats({
          totalScores: response.data.total_scores || 0,
          globalRank: response.data.global_rank || 0,
          recentScoresCount: response.data.recent_scores?.length || 0,
        });
      }
    } catch (err) {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        console.error('Failed to fetch individual data:', err);
      }
    } finally {
      if (
        requestGeneration === profileRequestGenerationRef.current &&
        requestProfileId === activeProfileIdRef.current
      ) {
        setIsLoading(false);
      }
    }
  }, [individualsApi, effectiveRole, activeProfileId]);

  useEffect(() => {
    fetchAffiliateData();
    fetchIndividualData();
  }, [fetchAffiliateData, fetchIndividualData]);

  // Load scores when Workouts tab is activated
  useEffect(() => {
    if (activeTab === 'workouts') {
      fetchMyScores();
    }
  }, [activeTab, fetchMyScores]);

  // HQ Admin gets a dedicated hub view (HQ-018 fix)
  // Placed AFTER all hooks to comply with Rules of Hooks
  if (effectiveRole === 'hq_admin') {
    const adminQuickLinks = [
      { label: 'Affiliates', icon: Building2, path: '/admin/affiliates', color: 'text-primary' },
      { label: 'Users', icon: Users, path: '/admin/users', color: 'text-blue-500' },
      { label: 'Programming', icon: Target, path: '/admin/programming', color: 'text-green-500' },
      { label: 'Events', icon: Calendar, path: '/admin/events', color: 'text-amber-500' },
      { label: 'Settings', icon: Settings, path: '/settings', color: 'text-muted-foreground' },
    ];

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            WELCOME, @{user?.username?.toUpperCase() || 'ADMIN'}
          </h1>
          <p className="text-muted-foreground">ErgShoot HQ Administrator</p>
        </div>

        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
          {adminQuickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Card
                key={link.path}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate(link.path)}
              >
                <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                  <Icon className={cn('h-8 w-8 mb-2', link.color)} />
                  <span className="text-sm font-medium">{link.label}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5 text-primary" />
              HQ Admin Hub
            </CardTitle>
            <CardDescription>Manage the ErgShoot platform from the admin dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/admin')} className="w-full sm:w-auto">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Go to Admin Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Stats wired to real data
  const stats =
    effectiveRole === 'individual'
      ? [
          {
            label: 'Total Scores',
            value: hubStats.totalScores,
            icon: Target,
            color: 'text-yellow-500',
          },
          {
            label: 'Global Rank',
            value: hubStats.globalRank || '—',
            icon: Trophy,
            color: 'text-yellow-500',
          },
          { label: 'Friends', value: 0, icon: Users, color: 'text-teal-500' },
          { label: 'Events Joined', value: 0, icon: Calendar, color: 'text-green-500' },
        ]
      : [
          {
            label: 'Scores Logged',
            value: hubStats.recentScoresCount,
            icon: Target,
            color: 'text-yellow-500',
          },
          { label: 'Challenges', value: 0, icon: Trophy, color: 'text-yellow-500' },
          { label: 'Friends', value: 0, icon: Users, color: 'text-teal-500' },
          { label: 'Events Joined', value: 0, icon: Calendar, color: 'text-green-500' },
        ];

  // Generate subtitle based on role and affiliate
  const getSubtitle = () => {
    // Individuals are never gym-affiliated — always show personal subtitle
    if (effectiveRole === 'individual') {
      return 'Your personal fitness journey';
    }
    if (affiliateName) {
      return `${roleLabels[effectiveRole || ''] || 'Member'} of ${affiliateName}`;
    }
    return 'Your personal fitness hub';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          WELCOME, @{user?.username?.toUpperCase() || 'ATHLETE'}
        </h1>
        <p className="text-muted-foreground">{getSubtitle()}</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap',
                activeTab === tab.id
                  ? 'bg-primary/10 text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.label}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </CardTitle>
                    <Icon className={cn('h-5 w-5', stat.color)} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stat.value}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Context Card — different for individual vs gym-affiliated */}
          {effectiveRole === 'individual' ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="h-5 w-5 text-primary" />
                  My Training
                </CardTitle>
                <CardDescription>Your solo training hub</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col h-auto py-3 gap-1"
                    onClick={() => navigate('/workouts')}
                  >
                    <Target className="h-4 w-4 text-primary" />
                    <span className="text-xs">Workouts</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col h-auto py-3 gap-1"
                    onClick={() => navigate('/standards')}
                  >
                    <Trophy className="h-4 w-4 text-primary" />
                    <span className="text-xs">Standards</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col h-auto py-3 gap-1"
                    onClick={() => navigate('/progress')}
                  >
                    <Zap className="h-4 w-4 text-primary" />
                    <span className="text-xs">Progress</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col h-auto py-3 gap-1"
                    onClick={() => navigate('/leaderboard')}
                  >
                    <Trophy className="h-4 w-4 text-primary" />
                    <span className="text-xs">Leaderboard</span>
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
                  <Badge variant="outline" className="text-xs">
                    Individual
                  </Badge>
                  <span>Train anywhere, compete globally</span>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5 text-primary" />
                  My Affiliate
                </CardTitle>
                <CardDescription>Your gym membership</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                ) : hasAffiliate ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-lg">{affiliateName || 'Your Gym'}</span>
                      <Badge variant="secondary" className="bg-primary/10 text-primary">
                        {roleLabels[effectiveRole || ''] || 'Member'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Connected and active</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No gym membership</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'workouts' && (
        <Card>
          <CardHeader>
            <CardTitle>My Workouts</CardTitle>
            <CardDescription>Your workout history and progress</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingScores && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoadingScores && scoresError && (
              <p className="text-sm text-destructive">{scoresError}</p>
            )}
            {!isLoadingScores && !scoresError && myScores.length === 0 && (
              <p className="text-muted-foreground">
                Your completed workouts will appear here. Log a score to get started.
              </p>
            )}
            {!isLoadingScores && !scoresError && myScores.length > 0 && (
              <ul className="divide-y">
                {myScores.map((s) => (
                  <li
                    key={s.id}
                    className="py-3 flex items-start justify-between gap-4 cursor-pointer hover:bg-muted/40 rounded px-2 -mx-2"
                    onClick={() => navigate('/workouts')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate('/workouts');
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{s.workout_name}</span>
                        <Badge variant="outline" className="text-xs uppercase">
                          {s.division}
                        </Badge>
                      </div>
                      {s.notes && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          &ldquo;{s.notes}&rdquo;
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(s.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-semibold tabular-nums">{s.score_display}</div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {s.scoring_type}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'friends' && <FriendsTab />}

      {activeTab === 'challenges' && (
        <Card>
          <CardHeader>
            <CardTitle>Challenges</CardTitle>
            <CardDescription>Compete and earn achievements</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-8">
            <Swords className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No active challenges</p>
            <p className="text-sm text-muted-foreground">Challenges coming soon</p>
          </CardContent>
        </Card>
      )}

      {activeTab === 'events' && (
        <Card>
          <CardHeader>
            <CardTitle>My Events</CardTitle>
            <CardDescription>Events you've joined</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No upcoming events</p>
            <p className="text-sm text-muted-foreground">Join events from the Events page</p>
          </CardContent>
        </Card>
      )}

      {activeTab === 'connect' && (
        <Card>
          <CardHeader>
            <CardTitle>Connect</CardTitle>
            <CardDescription>Connect your devices and apps</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-8">
            <Zap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Device integration coming soon</p>
            <p className="text-sm text-muted-foreground">Connect your Concept2 and other devices</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface Friend {
  id: string;
  name: string;
  email: string;
  role: string;
  friends_since: string;
}

interface FriendRequest {
  id: string;
  from_user_id: string;
  name: string;
  email: string;
  created_at: string;
}

interface DiscoverUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

function FriendsTab() {
  const { api } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DiscoverUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(true);

  // Fetch friends and requests on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [friendsRes, requestsRes] = await Promise.all([
          api.get<{ friends: Friend[] }>('/friends'),
          api.get<{ requests: FriendRequest[] }>('/friends/requests'),
        ]);
        if (friendsRes.success && friendsRes.data) setFriends(friendsRes.data.friends);
        if (requestsRes.success && requestsRes.data) setRequests(requestsRes.data.requests);
      } catch {
        // silently fail
      } finally {
        setLoadingFriends(false);
      }
    };
    fetchData();
  }, [api]);

  const handleSearch = async (q: string) => {
    setSearchQuery(q);
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await api.get<{ users: DiscoverUser[] }>(
        `/friends/discover?q=${encodeURIComponent(q)}`
      );
      if (res.success && res.data) setSearchResults(res.data.users);
    } catch {
      // silently fail
    } finally {
      setIsSearching(false);
    }
  };

  const sendRequest = async (userId: string) => {
    // BUG-APR07-010: previously errors were swallowed silently, leaving users
    // (especially individuals) staring at a button that did nothing.
    try {
      const res = await api.post<{ request_id?: string }>('/friends/request', {
        to_user_id: userId,
      });
      if (res.success) {
        setSearchResults((prev) => prev.filter((u) => u.id !== userId));
        toast.success('Friend request sent');
      } else {
        toast.error(res.error || 'Could not send friend request');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send friend request');
    }
  };

  const handleRequest = async (requestId: string, action: 'accept' | 'reject') => {
    try {
      const res = await api.patch(`/friends/request/${requestId}`, { action });
      if (!res.success) {
        toast.error(res.error || `Could not ${action} request`);
        return;
      }
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast.success(action === 'accept' ? 'Friend request accepted' : 'Friend request rejected');
      if (action === 'accept') {
        // Refresh friends list
        const listRes = await api.get<{ friends: Friend[] }>('/friends');
        if (listRes.success && listRes.data) setFriends(listRes.data.friends);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Could not ${action} request`);
    }
  };

  if (loadingFriends) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Find Athletes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {isSearching && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-2 rounded border border-border"
                >
                  <div>
                    <p className="text-sm font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => sendRequest(u.id)}>
                    <Users className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {requests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Friend Requests
              <Badge variant="secondary" className="ml-2">
                {requests.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {requests.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between p-2 rounded border border-border"
              >
                <div>
                  <p className="text-sm font-medium">{req.name}</p>
                  <p className="text-xs text-muted-foreground">{req.email}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => handleRequest(req.id, 'accept')}>
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRequest(req.id, 'reject')}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Friends List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            My Friends
            {friends.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {friends.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {friends.length === 0 ? (
            <div className="text-center py-6">
              <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">No friends yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Search for athletes above to send friend requests
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {friends.map((friend) => (
                <div
                  key={friend.id}
                  className="flex items-center justify-between p-2 rounded border border-border"
                >
                  <div>
                    <p className="text-sm font-medium">{friend.name}</p>
                    <p className="text-xs text-muted-foreground">{friend.email}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {friend.role}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
